import type { ContractValidation } from '@bk/contracts';
import type { Procedure, ProcessContract } from '@bk/process-dsl';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useTenant } from '../../lib/useTenant';
import { ErrorText } from '../../ui';
import { ContractView } from './ContractView';

/** Editor del contratto (YAML) con validazione dal server mentre si scrive e anteprima leggibile. */
export function ContractEditor({ source, onChange, onValid }: { source: string; onChange: (s: string) => void; onValid?: (ok: boolean) => void }) {
  const { t } = useTenant();
  const [result, setResult] = useState<ContractValidation | null>(null);
  const [failure, setFailure] = useState('');

  useEffect(() => {
    if (source.trim().length < 10) {
      setResult(null);
      onValid?.(false);
      return;
    }
    const timer = setTimeout(() => {
      api<ContractValidation>(t('/processes/validate'), { body: { source } })
        .then((r) => {
          setResult(r);
          setFailure('');
          onValid?.(r.ok);
        })
        .catch((e) => setFailure(String(e?.message ?? e)));
    }, 400);
    return () => clearTimeout(timer);
  }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <textarea
        className="h-[32rem] w-full rounded-lg border border-black/15 bg-white p-3 font-mono text-xs leading-relaxed outline-none focus:border-accent"
        spellCheck={false}
        value={source}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Contratto di processo (YAML)"
      />
      <div className="h-[32rem] overflow-auto rounded-lg border border-black/10 bg-white p-4">
        <ErrorText>{failure}</ErrorText>
        {!result && !failure && <p className="text-sm text-ink/50">L’anteprima compare qui mentre scrivi.</p>}
        {result && !result.ok && (
          <div>
            <p className="mb-2 text-sm font-semibold text-red-700">Da correggere</p>
            <ul className="space-y-1 text-sm">
              {result.issues.map((i, n) => (
                <li key={n} className="rounded bg-red-50 px-2 py-1 text-red-800">
                  {i.path && <code className="mr-1 text-xs">{i.path}</code>}
                  {i.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        {result?.ok && <ContractView contract={result.contract as ProcessContract} procedure={result.procedure as Procedure} />}
      </div>
    </div>
  );
}
